/**
 * Tinted glyph-run cache (03 §8, 09 §7).
 *
 * A run is rasterized once per (str, font, n, rgb, shadow) into its own canvas at `n` device px per
 * glyph px (`n` from `tierRun`, core/hud/type.ts), so drawing it is one 1:1 drawImage, never scaled.
 * `n` follows from the tier and the global `f`; the cache is cleared on `setScale`.
 *
 * LRU with a byte cap (RGBA, 4 B/px). Lookups are allocation-free (string → small entry list,
 * intrusive doubly linked LRU list). Fast-changing strings (clocks, gaps) should use the
 * glyph-by-glyph mode instead (`glyphAtlas`), which caches one tinted atlas per (font, rgb, n).
 * Runs and glyph atlases share one LRU list and one byte cap, so animated colours cannot grow the
 * cache without bound.
 */
import type { RGB } from '../api.ts';
import { type PixelFont, rasterizeRun } from './font.ts';

export type HudCanvas = OffscreenCanvas | HTMLCanvasElement;
export type CanvasFactory = (w: number, h: number) => HudCanvas;

export const defaultCanvasFactory: CanvasFactory = (w, h) => new OffscreenCanvas(Math.max(1, w), Math.max(1, h));

export interface TextRun {
  readonly str: string;
  readonly font: PixelFont;
  /** device px per glyph px */
  readonly n: number;
  readonly rgb: number;
  /** shadow rgb (24-bit) or −1 */
  readonly shadow: number;
  readonly canvas: HudCanvas;
  /** raster size, raster px */
  readonly w: number;
  readonly h: number;
  /** text-box origin inside the raster, raster px (= shadow pad) */
  readonly ox: number;
  readonly oy: number;
  readonly bytes: number;
}

interface Entry extends TextRun {
  /** glyph-atlas key, or −1 for a run */
  akey: number;
  prev: Entry | null;
  next: Entry | null;
}

export interface GlyphAtlas {
  readonly font: PixelFont;
  readonly rgb: number;
  readonly n: number;
  /** the whole font atlas tinted, scaled ×n (glyph (x, y, w, h) → (x·n, y·n, w·n, h·n)) */
  readonly canvas: HudCanvas;
  readonly bytes: number;
}

export function packRgb(c: RGB): number {
  return ((c[0] & 255) << 16) | ((c[1] & 255) << 8) | (c[2] & 255);
}

/** 2D context of a HUD canvas (throws when unavailable). */
export function ctx2d(c: HudCanvas): OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D {
  // A union receiver loses the '2d' overload, so narrow through one member type.
  const ctx = (c as OffscreenCanvas).getContext('2d') as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('hud: no 2d context');
  return ctx;
}

export interface TextCacheOptions {
  /** byte cap for runs + glyph atlases (default 16 MiB; H7/M4: counts against the iOS canvas cap) */
  maxBytes?: number;
  createCanvas?: CanvasFactory;
  /** build ImageData (injectable for node tests) */
  createImageData?: (data: Uint8ClampedArray<ArrayBuffer>, w: number, h: number) => ImageData;
}

export class TextCache {
  readonly maxBytes: number;
  private readonly createCanvas: CanvasFactory;
  private readonly createImageData: (data: Uint8ClampedArray<ArrayBuffer>, w: number, h: number) => ImageData;
  private readonly byStr = new Map<string, Entry[]>();
  private head: Entry | null = null;
  private tail: Entry | null = null;
  private _bytes = 0;
  private _count = 0;
  private _k = 0;
  private readonly atlases = new Map<number, Entry>();
  /** counters for tests / the debug overlay */
  hits = 0;
  misses = 0;

  constructor(opts: TextCacheOptions = {}) {
    // H7 (M4): 16 MiB — a 4K session needs well under half the old 48 MiB, and every run is its
    // own canvas on iOS (see the timing note in the r14 report)
    this.maxBytes = opts.maxBytes ?? 16 * 1024 * 1024;
    this.createCanvas = opts.createCanvas ?? defaultCanvasFactory;
    this.createImageData = opts.createImageData ?? ((d, w, h) => new ImageData(d, w, h));
  }

  get bytes(): number {
    return this._bytes;
  }
  /** cached runs */
  get size(): number {
    return this._count;
  }
  /** cached glyph atlases */
  get atlasCount(): number {
    return this.atlases.size;
  }
  /** Set the HUD scale `f`; clears everything when it changes (every run's `n` depends on it). */
  setScale(k: number): void {
    if (k !== this._k) {
      this.clear();
      this._k = k;
    }
  }

  clear(): void {
    this.byStr.clear();
    this.head = this.tail = null;
    this._bytes = 0;
    this._count = 0;
    this.atlases.clear();
  }

  /** Cached run (rasterized on a miss). Allocation-free on a hit. */
  run(font: PixelFont, str: string, n: number, rgb: RGB, shadow: RGB | null): TextRun {
    const c = packRgb(rgb);
    const sh = shadow ? packRgb(shadow) : -1;
    const list = this.byStr.get(str);
    if (list) {
      for (const e of list) {
        if (e.font === font && e.n === n && e.rgb === c && e.shadow === sh) {
          this.hits++;
          this.touch(e);
          return e;
        }
      }
    }
    this.misses++;
    const e = this.build(font, str, n, c, sh);
    if (list) list.push(e);
    else this.byStr.set(str, [e]);
    this.pushFront(e);
    this._bytes += e.bytes;
    this._count++;
    this.evict(e);
    return e;
  }

  /** Tinted, ×n-scaled copy of a font atlas for the glyph-by-glyph mode. */
  glyphAtlas(font: PixelFont, rgb: number, n: number): GlyphAtlas {
    const key = ((font.name === '3x5' ? 1 : font.name === '3x5x' ? 2 : 0) * 64 + n) * 16777216 + rgb;
    let a = this.atlases.get(key);
    if (a) {
      this.touch(a);
    } else {
      const W = font.atlasW * n;
      const H = font.atlasH * n;
      const data = new Uint8ClampedArray(W * H * 4);
      const r = (rgb >> 16) & 255;
      const g = (rgb >> 8) & 255;
      const b = rgb & 255;
      for (let y = 0; y < H; y++) {
        const ay = Math.floor(y / n) * font.atlasW;
        for (let x = 0; x < W; x++) {
          if (font.ink[ay + Math.floor(x / n)] !== 1) continue;
          const o = (y * W + x) * 4;
          data[o] = r;
          data[o + 1] = g;
          data[o + 2] = b;
          data[o + 3] = 255;
        }
      }
      const canvas = this.createCanvas(W, H);
      ctx2d(canvas).putImageData(this.createImageData(data, W, H), 0, 0);
      a = { str: '', font, n, rgb, shadow: -1, canvas, w: W, h: H, ox: 0, oy: 0, bytes: W * H * 4, akey: key, prev: null, next: null };
      this.atlases.set(key, a);
      this.pushFront(a);
      this._bytes += a.bytes;
      this.evict(a);
    }
    return a;
  }

  private build(font: PixelFont, str: string, n: number, rgb: number, sh: number): Entry {
    const r = rasterizeRun(font, str, 1, sh >= 0, n);
    const w = Math.max(1, r.w);
    const h = Math.max(1, r.h);
    const data = new Uint8ClampedArray(w * h * 4);
    for (let y = 0; y < r.h; y++) {
      for (let x = 0; x < r.w; x++) {
        const v = r.data[y * r.w + x];
        if (v === 0) continue;
        const c = v === 1 ? rgb : sh;
        const o = (y * w + x) * 4;
        data[o] = (c >> 16) & 255;
        data[o + 1] = (c >> 8) & 255;
        data[o + 2] = c & 255;
        data[o + 3] = 255;
      }
    }
    const canvas = this.createCanvas(w, h);
    ctx2d(canvas).putImageData(this.createImageData(data, w, h), 0, 0);
    return { str, font, n, rgb, shadow: sh, canvas, w: r.w, h: r.h, ox: r.ox, oy: r.oy, bytes: w * h * 4, akey: -1, prev: null, next: null };
  }

  private touch(e: Entry): void {
    if (this.head === e) return;
    this.unlink(e);
    this.pushFront(e);
  }

  private pushFront(e: Entry): void {
    e.prev = null;
    e.next = this.head;
    if (this.head) this.head.prev = e;
    this.head = e;
    this.tail ??= e;
  }

  private unlink(e: Entry): void {
    if (e.prev) e.prev.next = e.next;
    else this.head = e.next;
    if (e.next) e.next.prev = e.prev;
    else this.tail = e.prev;
    e.prev = e.next = null;
  }

  /** Drop least-recently-used runs/atlases until under the cap (never the one just built). */
  private evict(keep: Entry): void {
    while (this._bytes > this.maxBytes && this.tail && this.tail !== keep) {
      const e = this.tail;
      this.unlink(e);
      this._bytes -= e.bytes;
      if (e.akey >= 0) this.atlases.delete(e.akey);
      else {
        this._count--;
        const list = this.byStr.get(e.str);
        if (list) {
          const i = list.indexOf(e);
          if (i >= 0) list.splice(i, 1);
          if (list.length === 0) this.byStr.delete(e.str);
        }
      }
      if ('width' in e.canvas) {
        // release the backing store early
        e.canvas.width = 0;
        e.canvas.height = 0;
      }
    }
  }
}
